<template>
  <Drawer
    ref="drawer"
    variant="full"
    :open="props.open"
    class="[&>div:nth-child(2)]:!p-0 [&>div:last-of-type]:border-t"
    @close="close"
  >
    <template #content>
      <div class="flex flex-col overflow-y-auto grow">
        <div class="flex items-center gap-3 p-4 border-b">
          <div class="border border-gray-200 p-2.5 rounded-xl">
            <QrCodeTwo class="h-5 w-5 text-gray-900" aria-hidden="true" />
          </div>
          <span class="text-gray-900 text-lg font-semibold">
            {{
              props.details?.space?.name ||
              t("admin.space_overview.unknown_space")
            }}
          </span>
        </div>
        <nav
          class="flex w-full bg-gray-100 p-2.5 justify-around"
          v-if="activeTabId"
        >
          <div
            @click="activeTabId = tab.id"
            v-for="tab in tabs.filter((tab) => tab.visible)"
            :key="tab.name"
            :class="[
              tab.id === activeTabId
                ? 'bg-white text-gray-700'
                : 'text-gray-500',
              'flex items-center shrink-0 gap-1.5 rounded-sm px-3 py-2 text-sm font-medium',
            ]"
          >
            <span>{{ tab.name }}</span>
            <span
              v-if="tab.count"
              :class="[
                tab.id === activeTabId ? 'bg-gray-100' : 'bg-gray-200',
                'text-xs px-2 py-0.5 rounded-full',
              ]"
              >{{ tab.count }}</span
            >
          </div>
        </nav>
        <div class="p-4 overflow-y-scroll h-full">
          <QrCodeSpaceInformation
            v-if="activeTabId === 'information'"
            :space="props.details?.space"
            :can-update="canUpdateSpaceOrAsset"
            @update:details="emit('update:details', $event)"
          />
          <QrCodeDocuments
            v-if="activeTabId === 'documents'"
            :documents="props.details?.documents"
          />
          <QrCodeOtherCodes
            v-if="activeTabId === 'codes'"
            :codes="props.details?.qr_codes || []"
            :scanned-code="props.scannedCode"
            :can-update="canUpdateSpaceOrAsset"
            @close="emit('close')"
            @assign="assign"
            @scan="scan"
          />
          <div v-if="!activeTabId">
            {{ t("admin.space_overview.no_permission") }}
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex gap-3">
        <secondary-button
          class="w-full h-11 items-center"
          :label="t('admin.space_overview.assign')"
          @click="assign(props.scannedCode)"
        />
        <primary-button
          class="w-full h-11 items-center"
          :label="t('admin.space_overview.create_task')"
          @click="addTask"
        />
      </div>
    </template>
  </Drawer>
</template>

<script setup>
import { Drawer } from "@/Components/Drawer";
import QrCodeTwo from "@/Tailwind/Icon/QrCodeTwo.vue";
import { inject, ref, computed, watch } from "vue";
import QrCodeSpaceInformation from "@/Tailwind/Components/QrCodeSpaceInformation.vue";
import QrCodeDocuments from "@/Tailwind/Components/QrCodeDocuments.vue";
import QrCodeOtherCodes from "@/Tailwind/Components/QrCodeOtherCodes.vue";
import { useI18n } from "vue-i18n";
import PrimaryButton from "@/Tailwind/Components/Buttons/PrimaryButton.vue";
import SecondaryButton from "@/Tailwind/Components/Buttons/SecondaryButton.vue";

// Translations
const { t } = useI18n();

// Emits
const emit = defineEmits([
  "close",
  "assign",
  "scan",
  "add-task",
  "update:details",
]);

// Define Props
const props = defineProps({
  open: Boolean,
  details: Object,
  scannedCode: String,
});

// Permissions
const { hasPermission, permissions } = inject("permissions");

// Refs
const activeTabId = ref(undefined);
const drawer = ref(null);

// Computed Props
const tabs = computed(() => {
  return [
    {
      id: "information",
      name: t("admin.space_overview.tabs.information"),
      visible: props.details?.space,
    },
    {
      id: "documents",
      name: t("admin.space_overview.tabs.documents"),
      visible: props.details?.documents,
    },
    {
      id: "codes",
      name: t("admin.space_overview.tabs.qr_codes"),
      visible: props.details?.qr_codes,
    },
  ];
});

const canUpdateSpaceOrAsset = computed(() => {
  const space = props.details?.space;
  if (!space) {
    return;
  }

  if (space.type === "asset" || space.type === "asset_group") {
    return hasPermission(permissions.updateAsset);
  }

  return hasPermission(permissions.updateSpace);
});

// Watchers
watch(tabs, () => {
  activeTabId.value = tabs.value.find((tab) => tab.visible)?.id;
});

// Functions
const assign = (id) => {
  emit("assign", id);
};

const scan = () => {
  emit("scan");
};

const addTask = () => {
  emit("add-task", {
    spaceId: props.details?.space.id,
    buildingId: props.details?.space.tenant_id,
  });
};

const close = () => {
  emit("close");
};

defineExpose({
  close: () => drawer.value?.close(),
});
</script>
